import { trackSegment } from 'utils/segment';
import { routePaths } from 'components/Routes/data';
import { ordinalSuffix } from 'utils/Numbers';
import Allowance from 'utils/Allowance';

export const handleBackToSetupGuide = () => {
  trackSegment('onboarding.company_details.back_to_setup_guide_click', { queue: true });
  window.location.href = routePaths.phpRoutes.dashboard;
};

export const getPayrollDateBanner = (date: number): string => {
  if (date === 31) {
    return "Employees will be paid on the last day of the month for that month's work.";
  }
  if (date <= 15) {
    return `Employees will be paid on the ${ordinalSuffix(date)} of the following month for the previous month's work.`;
  }
  return `Employees will be paid on the ${ordinalSuffix(date)} of the same month for that month's work.`;
};

export const getDefaultSalaryStructureTableNodes = ({
  organizationDefaultSalaryStructure,
}: {
  organizationDefaultSalaryStructure: {
    basicPercentage?: number;
    hraPercentage?: number;
    specialAllowancePercentage?: number;
    ltaPercentage?: number;
  };
}) => [
  {
    id: 'basic',
    component: 'Basic Salary',
    percentage: organizationDefaultSalaryStructure.basicPercentage,
  },
  {
    id: 'hra',
    component: 'HRA',
    percentage: organizationDefaultSalaryStructure.hraPercentage,
  },
  {
    id: 'special',
    component: 'Special Allowance',
    percentage: organizationDefaultSalaryStructure.specialAllowancePercentage,
  },
  {
    id: 'lta',
    component: 'LTA',
    percentage: organizationDefaultSalaryStructure.ltaPercentage,
  },
];

// Keys of the fixed (predefined) salary components the form tracks.
// Mirrors the `FixedFields` shape declared in SalaryStructure/index.tsx.
type CustomSalaryStateKey =
  | 'customBasic'
  | 'customDa'
  | 'customHra'
  | 'customSpecialAllowance'
  | 'customLta'
  | 'customNpsLimit';

// Row for a predefined salary component (Basic, DA, HRA, etc.).
type FixedSalaryStructureRow = {
  id: string;
  kind: 'fixed';
  label: string;
  stateKey: CustomSalaryStateKey;
  defaultValue: string;
  taxable: 'Yes' | 'No' | 'PartiallyHRA';
};

// Row for a user-defined custom allowance (free-form name + value).
type CustomAllowanceRow = {
  id: string;
  kind: 'custom';
  idx: number;
  allowance: Allowance;
};

// Discriminated union — `kind` lets the renderer narrow to the right shape.
export type CustomSalaryStructureRow = FixedSalaryStructureRow | CustomAllowanceRow;

export const getCustomSalaryStructureTableNodes = ({
  salaryStructureConfig,
  isEmployerNPSEnabled,
  customAllowances,
}: {
  salaryStructureConfig?: {
    percBasic?: number | null;
    percDa?: number | null;
    percHra?: number | null;
    percSpecialAllowance?: number | null;
    percLta?: number | null;
    percNpsLimit?: number | null;
  } | null;
  isEmployerNPSEnabled: boolean;
  customAllowances: Allowance[];
}): CustomSalaryStructureRow[] => [
  {
    id: 'basic',
    kind: 'fixed',
    label: 'Basic Salary',
    stateKey: 'customBasic',
    defaultValue: String(salaryStructureConfig?.percBasic || 0),
    taxable: 'Yes',
  },
  {
    id: 'da',
    kind: 'fixed',
    label: 'DA',
    stateKey: 'customDa',
    defaultValue: String(salaryStructureConfig?.percDa || 0),
    taxable: 'Yes',
  },
  {
    id: 'hra',
    kind: 'fixed',
    label: 'HRA',
    stateKey: 'customHra',
    defaultValue: String(salaryStructureConfig?.percHra || 0),
    taxable: 'PartiallyHRA',
  },
  {
    id: 'special',
    kind: 'fixed',
    label: 'Special Allowance',
    stateKey: 'customSpecialAllowance',
    defaultValue: String(salaryStructureConfig?.percSpecialAllowance || 0),
    taxable: 'Yes',
  },
  {
    id: 'lta',
    kind: 'fixed',
    label: 'LTA',
    stateKey: 'customLta',
    defaultValue: String(salaryStructureConfig?.percLta || 0),
    taxable: 'Yes',
  },
  ...(isEmployerNPSEnabled
    ? [
        {
          id: 'nps',
          kind: 'fixed',
          label: 'Employer NPS Contribution',
          stateKey: 'customNpsLimit',
          defaultValue: String(salaryStructureConfig?.percNpsLimit || 0),
          taxable: 'No',
        } satisfies FixedSalaryStructureRow,
      ]
    : []),
  ...customAllowances.map(
    (allowance, idx): CustomAllowanceRow => ({
      id: `custom-${idx}`,
      kind: 'custom',
      idx,
      allowance,
    }),
  ),
];

export const wordWrapText = (text: string, maxLineLength: number = 80) => {
  if (!text) return ['', ''];
  if (text.length <= maxLineLength) return [text, ''];
  const words = text.split(' ');
  let lineLength = 0;

  const formatted = words.reduce((result, word) => {
    if (lineLength + word.length >= maxLineLength) {
      lineLength = word.length;
      return result + `\n${word}`;
    } else {
      lineLength += word.length + (result ? 1 : 0);
      return result ? result + ` ${word}` : `${word}`;
    }
  }, '');
  const lines = formatted.split('\n');
  if (lines.length === 1) lines.push('');
  return lines;
};
